import { useRef } from "react";
import { t, bi, formatDate } from "../shared/trendReportShared";
import ReportDateFilter from "./ReportDateFilter";

// Purely presentational: TrendReportsFeedPage.js owns the fetch (GET
// /trend-reports/list) and the page / date range (kept in the URL), and hands
// them down, so this file only renders what it's given and reports changes.

function ReportCard({ report, isLatest, language, onOpen }) {
  const title = bi(report.title, language) || t("fallbackTitle", language);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex flex-col text-left rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 hover:shadow-md hover:border-orange-200 dark:hover:border-orange-500/40 transition-all"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {formatDate(report.report_date, language)}
        </span>
        {isLatest && (
          <span className="text-[10px] font-bold uppercase tracking-wide text-orange-500 bg-orange-50 dark:bg-orange-500/10 rounded-full px-2 py-0.5">
            {t("latest", language)}
          </span>
        )}
      </div>
      <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-snug mb-3 group-hover:text-orange-600 dark:group-hover:text-orange-400">
        {title}
      </h3>
      <div className="mt-auto flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span>
            {report.stats?.total_items ?? 0} {t("items", language)}
          </span>
          <span aria-hidden>·</span>
          <span>
            {report.stats?.applications_with_news ?? report.stats?.topics_with_news ?? 0}{" "}
            {t("applicationsWithNews", language)}
          </span>
        </div>
        <span className="text-xs font-semibold text-orange-600 dark:text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          {t("readReport", language)} →
        </span>
      </div>
    </button>
  );
}

// Same pager as the product list (tabs/products/DatabaseView.js), plus dark
// mode to match the report cards.
function Pager({ pagination, loading, language, onPageChange }) {
  return (
    <div className="mt-6 flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl px-6 py-4 border border-gray-200 dark:border-gray-700 shadow-sm">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {language === "zh"
          ? `第 ${pagination.page} 頁，共 ${pagination.pages} 頁 (${pagination.total} 份報告)`
          : `Page ${pagination.page} of ${pagination.pages} (${pagination.total} reports)`}
      </p>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(pagination.page - 1)}
          disabled={pagination.page <= 1 || loading}
          className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-orange-50 dark:hover:bg-orange-500/10 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {Array.from({ length: Math.min(pagination.pages, 7) }, (_, i) => {
          // Show pages around the current page
          let p;
          if (pagination.pages <= 7) {
            p = i + 1;
          } else if (pagination.page <= 4) {
            p = i + 1;
          } else if (pagination.page >= pagination.pages - 3) {
            p = pagination.pages - 6 + i;
          } else {
            p = pagination.page - 3 + i;
          }
          return (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              disabled={loading}
              className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                p === pagination.page
                  ? "bg-orange-500 text-white shadow-sm"
                  : "text-gray-600 dark:text-gray-300 hover:bg-orange-50 dark:hover:bg-orange-500/10 hover:text-orange-500"
              }`}
            >
              {p}
            </button>
          );
        })}

        <button
          onClick={() => onPageChange(pagination.page + 1)}
          disabled={pagination.page >= pagination.pages || loading}
          className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-orange-50 dark:hover:bg-orange-500/10 hover:text-orange-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default function TrendList({
  reports,
  status,
  pagination,
  latestDate,
  dateFrom,
  dateTo,
  language,
  onDateChange,
  onPageChange,
  onOpen,
}) {
  const topRef = useRef(null);
  const loading = status === "loading";
  const filtered = Boolean(dateFrom || dateTo);

  // The pager sits under the grid, so bring the new page's first row into view.
  const changePage = (p) => {
    onPageChange(p);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div ref={topRef} className="animate-fadeIn scroll-mt-4">
      <ReportDateFilter
        dateFrom={dateFrom}
        dateTo={dateTo}
        language={language}
        loading={loading}
        onDateChange={onDateChange}
      />

      {/* On a page or range change the old cards stay up (dimmed) instead of
          flashing to "Loading…"; the text only shows on the first load. */}
      {loading && reports.length === 0 && (
        <p className="text-sm text-gray-400">{t("loading", language)}</p>
      )}
      {status === "error" && (
        <p className="text-sm text-red-500">{t("listError", language)}</p>
      )}
      {status === "badRange" && (
        <p className="text-sm text-red-500">{t("badDateRange", language)}</p>
      )}
      {status === "ok" && reports.length === 0 && (
        <p className="text-sm text-gray-400">
          {t(filtered ? "noReportsInRange" : "noReportsYet", language)}
        </p>
      )}
      {(status === "ok" || loading) && reports.length > 0 && (
        <>
          <div
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity ${
              loading ? "opacity-60" : ""
            }`}
          >
            {reports.map((r) => (
              <ReportCard
                key={r.report_date}
                report={r}
                isLatest={r.report_date === latestDate}
                language={language}
                onOpen={() => onOpen(r.report_date)}
              />
            ))}
          </div>
          {pagination.pages > 1 && (
            <Pager
              pagination={pagination}
              loading={loading}
              language={language}
              onPageChange={changePage}
            />
          )}
        </>
      )}
    </div>
  );
}
